import React from 'react';
import styled from 'styled-components';
import { FaChevronRight } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';

import { useScreenQuery, Loader } from 'playup-web-core';

import ResponsibleGambling from '../../../User/components/ResponsibleGambling';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import formatCurrency from '../../../utils/formatCurrency';

import SecureTransaction from '../USFooter/SecureTransaction';
import QuestionsAndHelp from '../USFooter/QuestionsAndHelp';
import { WithdrawMethods } from './USWithdrawService';
import useNotify from '../../../hooks/useNotify';
import { createPaysafeWithdrawRegistrationRequest } from '../../../api/users';
import { BodyContainer } from './USWithdraw';

const USWithdrawPaymentSelection = ({
	availableWithdrawMethods,
	availableWithdrawMethodSummaries,
	setSelectedWithdrawMethod,
}: Props) => {
	const { user } = React.useContext(PortalSessionContext);
	const { isSmall } = useScreenQuery();
	const history = useHistory();
	const notify = useNotify();

	const selectWithdrawalMethod = async (selectedPaymentMethodId: number) => {
		const selectedWithdrawMethod = availableWithdrawMethodSummaries.find((condition) => {
			return condition.withdrawal_request_type.id === selectedPaymentMethodId;
		});
		const selectedInternalWithdrawMethod = Object.values(WithdrawMethods).find((m) => {
			return m.id === selectedWithdrawMethod?.withdrawal_request_type.id;
		});
		if (!selectedWithdrawMethod || !selectedInternalWithdrawMethod) {
			// Selected method is not available to this user, shouldn't happen
			notify({
				type: 'danger',
				title: 'Error',
				message: `The selected withdraw method is not available, please contact customer service for help.`,
				timeout: 5000,
			});
		}

		// Vip-preferred, Skrill, and Sightline can all be handled the same as below
		if (
			selectedWithdrawMethod?.withdrawal_request_type?.is_registrable === false &&
			!selectedWithdrawMethod?.user_paysafe_registration
		) {
			// User doesn't have a platform account and is not qualified to register
			notify({
				type: 'danger',
				title: 'Error',
				message: `Unable to create a ${selectedInternalWithdrawMethod?.name} account, please contact customer service for help.`,
				timeout: 5000,
			});
			return;
		} else if (
			selectedWithdrawMethod?.withdrawal_request_type?.is_registrable === true &&
			!selectedWithdrawMethod?.user_paysafe_registration
		) {
			// User doesn't have a platform account and redirect user to register
			const response = await createPaysafeWithdrawRegistrationRequest(selectedPaymentMethodId);
			if (response?.data?.redirect_registration) {
				history.push({
					pathname: `/enrollment/${selectedInternalWithdrawMethod?.name}`,
					state: { registrationLink: response?.data?.redirect_registration },
				});
				return;
			}
		} else if (selectedWithdrawMethod?.user_paysafe_registration) {
			// User has a platform account and proceed to deposit
			setSelectedWithdrawMethod(selectedWithdrawMethod);
			history.push(`/request/${selectedInternalWithdrawMethod?.name}`);
		}
	};

	return (
		<>
			<BalanceSection isSmallScreen={isSmall}>
				<BalanceType borderRight={true}>
					<BalanceName>Balance</BalanceName>
					<BalanceAmount isSmallScreen={isSmall}>{formatCurrency(user?.balance)}</BalanceAmount>
				</BalanceType>
				<BalanceType>
					<BalanceName>Withdrawal Balance</BalanceName>
					<BalanceAmount isSmallScreen={isSmall}>{formatCurrency(user?.available_balance)}</BalanceAmount>
				</BalanceType>
			</BalanceSection>
			<BodyContainer isSmallScreen={isSmall}>
				<OptionsHeader isSmallScreen={isSmall}>
					<NormalText>Select Destination Account</NormalText>
				</OptionsHeader>
				<WithdrawOptionsContainer>
					{availableWithdrawMethods?.length > 0 ? (
						availableWithdrawMethods.map((paymentMethod) => (
							<WithdrawOptionRow key={paymentMethod.id} onClick={() => selectWithdrawalMethod(paymentMethod.id)}>
								<LeftContainer>
									<LogoContainer>{paymentMethod.icon}</LogoContainer>
									<NormalText>{paymentMethod.name}</NormalText>
								</LeftContainer>
								<FaChevronRight />
							</WithdrawOptionRow>
						))
					) : (
						<Loader size={40} color={'#000000'} margin={'10px'} />
					)}
				</WithdrawOptionsContainer>
				<SecureTransaction />
				<QuestionsAndHelp />
				<ResponsibleGambling />
			</BodyContainer>
		</>
	);
};

export default USWithdrawPaymentSelection;

interface Props {
	availableWithdrawMethods: Array<any>;
	availableWithdrawMethodSummaries: Array<WithdrawalRequestType>;
	setSelectedWithdrawMethod: (method: WithdrawalRequestType) => void;
}

// This part is from web-core, change both to stay consistent ======
const BalanceSection = styled.section<{ isSmallScreen: boolean }>`
	display: flex;
	flex-direction: row;
	width: 100%;
	margin-top: ${({ isSmallScreen }) => (isSmallScreen ? '25px' : '30px')};
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '5px' : '15px')};
`;
const BalanceType = styled.div<{ borderRight?: boolean }>`
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;

	${({ borderRight }) => borderRight && `border-right: 2px solid;`}
	border-color: ${({ theme }) => theme.colors.lightGreyR}
`;

const BalanceName = styled.p`
	color: ${({ theme }) => theme.colors.greyR};
	text-align: center;
`;

const BalanceAmount = styled.p<{ isSmallScreen: boolean }>`
	color: ${({ theme }) => theme.colors.darkR};
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '22px' : '30px')};
	font-weight: 600;
	text-align: center;
`;
// This part is from web-core, change both to stay consistent ======

const OptionsHeader = styled.div<{ isSmallScreen: boolean }>`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: 40px;
	background-color: #dde1e3;
`;

const NormalText = styled.p`
	color: ${({ theme }) => theme.colors.darkR};
	margin: 0;
`;

const WithdrawOptionsContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`;

const WithdrawOptionRow = styled.button`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 60px;

	&:focus {
		outline: none;
	}

	&:not(:first-of-type) {
		border-top: 1px solid #dde1e3;
	}
`;

const LeftContainer = styled.div`
	display: flex;
	align-items: center;
`;

const LogoContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 110px;

	img {
		max-height: 30px;
	}
`;
